import {defineMessages, FormattedMessage} from 'react-intl';
import {PropertyList, PropertyListItem} from '@sail/ui/beta';
import {view, Badge} from '@sail/ui';
import {formatUtcDate} from '../MerchantPayoutStatus/date';
import {TransactionDetails} from './LinkDeclines';

const messages = defineMessages({
  transactionAmountTitle: {
    id: 'mse_inline_data.link_declines.transaction_amount_title',
    description: 'Header displayed above the transaction amount.',
    defaultMessage: 'Amount',
  },
  postDateTitle: {
    id: 'mse_inline_data.link_declines.post_date_title',
    description: 'Header displayed above failed transaction post date.',
    defaultMessage: 'Post date',
  },
  transactionStatusTitle: {
    id: 'mse_inline_data.link_declines.transaction_status_title',
    description: 'Header displayed above the transaction status.',
    defaultMessage: 'Transaction status',
  },
  failedBadgeTitle: {
    id: 'mse_inline_data.link_declines.failed_badge_title',
    description: 'Header displayed on the failed badge.',
    defaultMessage: 'Failed',
  },
});

const declineBlockingRationales = defineMessages({
  insufficentFundsSummary: {
    id: 'mse_inline_data.link_declines.insufficient_funds_summary',
    description: 'Summary of the insufficient funds decline code',
    defaultMessage: 'Your transaction failed due to insufficient funds.',
  },
  pinTryExceededSummary: {
    id: 'mse_inline_data.link_declines.pin_try_exceeded_summary',
    description: 'Summary of the pin try exceeded decline code',
    defaultMessage:
      'Your pin was entered too many times and caused the transaction to fail.',
  },
  cardVelocityExceededSummary: {
    id: 'mse_inline_data.link_declines.card_velocity_exceeded_summary',
    description: 'Summary of the card velocity exceeded decline code',
    defaultMessage: 'Too many attempts caused the transaction to fail.',
  },
  expiredCardSummary: {
    id: 'mse_inline_data.link_declines.expired_card_summary',
    description: 'Summary of the expired card decline code',
    defaultMessage: 'Your card is expired.',
  },
  incorrectNumberSummary: {
    id: 'mse_inline_data.link_declines.incorrect_number_summary',
    description: 'Summary of the incorrect number decline code',
    defaultMessage: 'Incorrect card details caused the transaction to fail.',
  },
  cardNotSupportedSummary: {
    id: 'mse_inline_data.link_declines.card_not_supported_summary',
    description: 'Summary of the card not supported decline code',
    defaultMessage: "Your card doesn't support this type of purchase.",
  },
  invalidCVCSummary: {
    id: 'mse_inline_data.link_declines.invalid_cvc_summary',
    description: 'Summary of the invalid CVC decline code',
    defaultMessage: 'An incorrect CVC caused the transaction to fail.',
  },
  invalidPinSummary: {
    id: 'mse_inline_data.link_declines.invalid_pin_summary',
    description: 'Summary of the invalid PIN decline code',
    defaultMessage: 'An incorrect pin caused the transaction to fail.',
  },
  incorrectZipSummary: {
    id: 'mse_inline_data.link_declines.incorrect_zip_summary',
    description: 'Summary of the incorrect ZIP decline code',
    defaultMessage: 'An incorrect zip code caused the transaction to fail.',
  },
  currencyNotSupportedSummary: {
    id: 'mse_inline_data.link_declines.currency_not_supported_summary',
    description: 'Summary of the currency not supported decline code',
    defaultMessage: "Your card doesn't support this currency.",
  },
  genericDeclineShortSummary: {
    id: 'mse_inline_data.link_declines.generic_decline_summary',
    description: 'Summary of the generic decline code',
    defaultMessage: 'Your card or payment method was declined.',
  },
  genericDeclineLongSummary: {
    id: 'mse_inline_data.link_declines.generic_error_summary',
    description: 'Summary of the generic error code',
    defaultMessage:
      'An error with your card or payment method caused the transaction to fail.',
  },
  genericProcessingErrorSummary: {
    id: 'mse_inline_data.link_declines.generic_processing_error_summary',
    description: 'Summary of the generic processing error code',
    defaultMessage: 'An error occurred while processing this payment.',
  },
});

interface DeclineMessage {
  id: string;
  description: string;
  defaultMessage: string;
}

const getDeclineDetails = (declineCode: string | undefined) => {
  // Map of decline codes with specific unblocking rationale
  const declineMessages: Record<string, DeclineMessage> = {
    insufficient_funds: declineBlockingRationales.insufficentFundsSummary,
    card_velocity_exceeded:
      declineBlockingRationales.cardVelocityExceededSummary,
    expired_card: declineBlockingRationales.expiredCardSummary,
    incorrect_number: declineBlockingRationales.incorrectNumberSummary,
    card_not_supported: declineBlockingRationales.cardNotSupportedSummary,
    do_not_honor: declineBlockingRationales.cardNotSupportedSummary,
    invalid_cvc: declineBlockingRationales.invalidCVCSummary,
    incorrect_cvc: declineBlockingRationales.invalidCVCSummary,
    invalid_pin: declineBlockingRationales.invalidPinSummary,
    incorrect_zip: declineBlockingRationales.incorrectZipSummary,
    currency_not_supported:
      declineBlockingRationales.currencyNotSupportedSummary,
    processing_error: declineBlockingRationales.genericProcessingErrorSummary,
  };

  // Set of decline codes that have a short version of the generic decline message
  const shortDeclines = [
    'generic_decline',
    'transaction_not_allowed',
    'invalid_account',
    'lost_card',
    'stolen_card',
    'fraudulent',
    'restricted_card',
    'revocation_of_authorization',
    'revocation_of_all_authorizations',
    'authentication_not_handled',
    'not_permitted',
  ];

  let declineMessage;

  if (declineCode && declineMessages[declineCode]) {
    declineMessage = declineMessages[declineCode];
  } else if (declineCode && shortDeclines.includes(declineCode)) {
    declineMessage = declineBlockingRationales.genericDeclineShortSummary;
  } else {
    // nil or invalid decline codes will show the generic decline message
    declineMessage = declineBlockingRationales.genericDeclineLongSummary;
  }

  return <FormattedMessage {...declineMessage} />;
};

export const TransactionDetailsSection = ({
  transactionDetails,
  declineCode,
}: {
  transactionDetails: TransactionDetails;
  declineCode: string | undefined;
}) => {
  const localizedDate = formatUtcDate(transactionDetails.transaction_date);

  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      <PropertyList>
        <PropertyListItem
          label={
            <view.div css={{font: 'label.medium.emphasized'}}>
              <FormattedMessage {...messages.transactionAmountTitle} />
            </view.div>
          }
          value={
            <view.div
              css={{stack: 'x', gap: 'xsmall'}}
              data-testid="link-declines-transaction-amount"
            >
              <view.span css={{font: 'heading.medium'}}>
                {transactionDetails.transaction_amount}
              </view.span>
              <view.span
                css={{font: 'heading.medium.subdued', textColor: 'subdued'}}
              >
                {transactionDetails.transaction_currency.toLocaleUpperCase()}
              </view.span>
            </view.div>
          }
        />
        <PropertyListItem
          label={
            <view.div css={{font: 'label.medium.emphasized'}}>
              <FormattedMessage {...messages.postDateTitle} />
            </view.div>
          }
          value={localizedDate}
        />
        <PropertyListItem
          label={
            <view.div css={{font: 'label.medium.emphasized'}}>
              <FormattedMessage {...messages.transactionStatusTitle} />
            </view.div>
          }
          value={
            <view.div
              css={{font: 'label.medium.emphasized'}}
              data-testid="link-declines-transaction-status"
            >
              <Badge type="negative">
                <FormattedMessage {...messages.failedBadgeTitle} />
              </Badge>
            </view.div>
          }
        />
      </PropertyList>
      <view.div
        data-testid="link-declines-transaction-decline-details"
        css={{font: 'label.small'}}
      >
        {getDeclineDetails(declineCode)}
      </view.div>
    </view.div>
  );
};
